import React from 'react';
import { ArrowRight, Wallet, ArrowDownUp, GitMerge, ArrowLeft } from 'lucide-react';
import { Link } from 'react-router-dom';
import maiado from '../../assets/images/maiadao.jpg';

const EvmBuyPage = () => {
  return (
    <div className="bg-gradient-to-r from-sky-50 via-sky-50/80 to-white min-h-screen">
      <div className="max-w-8xl mx-auto px-4 pt-6 pb-8 md:py-16">
        {/* Back Button */}
        <Link 
          to="/buy"
          className="inline-flex items-center gap-2 text-sky-500 hover:text-sky-600 transition-colors mb-8 md:mb-12"
        >
          <ArrowLeft className="w-5 h-5" />
          <span className="font-medium">Back to options</span>
        </Link>

        <div className="flex flex-col lg:flex-row">
          {/* Left side - Content */}
          <div className="w-full lg:w-3/5 pr-0 lg:pr-8">
            <div className="text-center mb-8 md:mb-12">
              <h1 className="text-3xl md:text-4xl font-bold text-sky-500 mb-2 md:mb-4">Buy $BOOP with HermesOmnichain</h1>
              <p className="text-gray-600 text-base md:text-lg">Follow these steps to purchase BOOP tokens using the HermesOmnichain swap tool</p>
            </div>

            <div className="space-y-4 md:space-y-6">
              {/* Step 1 */}
              <div className="bg-white/80 backdrop-blur-sm rounded-xl p-4 md:p-6 hover:bg-white border border-sky-200 hover:border-sky-300 transition-all shadow-sm">
                <div className="flex flex-col md:flex-row items-start gap-4">
                  <div className="bg-sky-50 rounded-lg p-3">
                    <ArrowDownUp className="w-6 h-6 text-sky-500" />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-xl font-semibold text-sky-500 mb-2">1. Load the HermesOmnichain swap tool</h3>
                    <p className="text-gray-600 mb-4">Navigate to the HermesOmnichain app page, powered by MaiaDAO:</p>
                    <a
                      href="https://app.maiadao.io/"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="bg-white/50 hover:bg-white text-sky-500 rounded-lg p-4 flex items-center justify-between group border border-sky-200 hover:border-sky-300"
                    >
                      <div>
                        <span className="font-medium">Launch HermesOmnichain</span>
                        <p className="text-sm text-gray-500 mt-1">app.maiadao.io</p>
                      </div>
                      <ArrowRight className="w-5 h-5 text-sky-400 group-hover:text-sky-500 transform group-hover:translate-x-1 transition-all" />
                    </a>
                  </div>
                </div>
              </div>

              {/* Step 2 */}
              <div className="bg-white/80 backdrop-blur-sm rounded-xl p-4 md:p-6 hover:bg-white border border-sky-200 hover:border-sky-300 transition-all shadow-sm">
                <div className="flex flex-col md:flex-row items-start gap-4">
                  <div className="bg-sky-50 rounded-lg p-3">
                    <Wallet className="w-6 h-6 text-sky-500" />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-xl font-semibold text-sky-500 mb-2">2. Connect your wallet</h3>
                    <div className="space-y-3 text-gray-600">
                      <p>• Any EVM compatible wallet will work</p>
                      <p>• Make sure the wallet supports your source network and the Arbitrum network</p>
                      <p>• Make sure you have some ETH in your source wallet to execute the trade</p>
                      <p>• If not, withdraw some ETH through a CEX, or buy directly through your wallet's interface (if supported)</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Step 3 */}
              <div className="bg-white/80 backdrop-blur-sm rounded-xl p-4 md:p-6 hover:bg-white border border-sky-200 hover:border-sky-300 transition-all shadow-sm">
                <div className="flex flex-col md:flex-row items-start gap-4">
                  <div className="bg-sky-50 rounded-lg p-3">
                    <GitMerge className="w-6 h-6 text-sky-500" />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-xl font-semibold text-sky-500 mb-2">3. Setup your trade</h3>
                    <div className="space-y-3 text-gray-600">
                      <p>From:</p>
                      <div className="pl-4">
                        <p>• Select your Source network and your Source currency</p>
                      </div>
                      <p>To:</p>
                      <div className="pl-4">
                        <p>• Select Arbitrum as the network and search for BOOP as the currency</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Step 4 */}
              <div className="bg-white/80 backdrop-blur-sm rounded-xl p-4 md:p-6 hover:bg-white border border-sky-200 hover:border-sky-300 transition-all shadow-sm">
                <div className="flex flex-col md:flex-row items-start gap-4">
                  <div className="bg-sky-50 rounded-lg p-3">
                    <ArrowRight className="w-6 h-6 text-sky-500" />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-xl font-semibold text-sky-500 mb-2">4. Execute the trade</h3>
                    <div className="space-y-3 text-gray-600">
                      <p>• Once the platform gives you a quote, execute the trade</p>
                      <p>• The platform will handle all bridging and swapping transactions for you</p>
                      <p>• The BOOP tokens will be delivered to the same wallet address as the Source wallet on the Arbitrum network</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Right side - Image */}
          <div className="w-full lg:w-2/5 p-4 md:p-12 mt-8 lg:mt-0">
            <div className="text-center mb-4 md:mb-6">
              <h3 className="text-xl font-semibold text-sky-500 mb-2">Example Interface</h3>
              <p className="text-gray-600">This is how the MaiaDAO interface looks like</p>
            </div>
            <div className="bg-white/80 backdrop-blur-sm p-4 md:p-6 rounded-2xl shadow-md border border-sky-200">
              <img 
                src={maiado} 
                alt="MaiaDAO Interface" 
                className="rounded-xl shadow-sm w-full h-auto"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EvmBuyPage;